import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Button, Progress, Select, Tooltip } from 'antd'
import { TOKEN_ICON_MAPPING, TOKEN_WITHDRAW } from 'config/constants'
import { UserDataResp } from 'config/types/Authentication'
import { WalletType } from 'config/types/Wallet'
import { useTranslation } from 'contexts/Localization'
import { formatCode } from 'helpers/CommonHelper'
import { useTheme } from 'next-themes'
import Link from 'next/link'
import { useEffect, Dispatch, SetStateAction, useMemo, useState } from 'react'
import styled from 'styled-components'
import { addCommasToNumber } from 'utils'
import { ArrowBackIcon, Copy2Icon } from 'widgets'
import { getPriceMBCOrigin } from 'state/nfts/api'
const Wrapper = styled.div`
  width: 100%;

  .ant-select-selector {
    padding: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }

  .token {
    p {
      font-size: 16px;
      font-weight: 700;
      line-height: 1.4;

      ${({ theme }) => theme.mediaQueries.lg} {
        font-size: 18px;
      }

      &.value {
        text-align: right;
      }

      &.rate {
        color: #989996;
        font-size: 12px;
        font-weight: 400;
        text-align: right;

        ${({ theme }) => theme.mediaQueries.lg} {
          font-size: 14px;
        }
      }
    }
  }
`

type FundingViewPropType = {
  userData?: UserDataResp
  myWallet: WalletType
  balances: any
  opvPrice: number
  usdtPrice: number
  perCentBalance: number
  setParamBalance: Dispatch<
    SetStateAction<{
      range: number
      type: string
    }>
  >
}

const FundingView = ({
  userData,
  myWallet,
  balances,
  opvPrice,
  usdtPrice,
  perCentBalance,
  setParamBalance,
}: FundingViewPropType) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [mbcPrice, setMbcPrice] = useState<any>(null)
  useEffect(() => {
    getPriceMBCOrigin().then((res) => {
      setMbcPrice(res.price)
    })
  }, [])
  const { usdtBalance, opvBalance, balanceData, percentUsdt, percentOpv } = useMemo(() => {
    const usdtAvailableBalance = balances
      ?.filter((item) => item?.tokenSymbol === 'USDT')
      ?.reduce(
        (total, cur) => total + cur?.available,

        0,
      )
    const opvAvailableBalance = balances?.find((item) => item?.tokenSymbol === 'OPV')?.available

    const totalUsd = (usdtAvailableBalance + opvAvailableBalance * mbcPrice) * (usdtPrice || 1)

    const assetAllocationUsdt = ((usdtAvailableBalance * (usdtPrice || 1)) / totalUsd) * 100
    const assetAllocationOpv = ((opvAvailableBalance * mbcPrice * (usdtPrice || 1)) / totalUsd) * 100

    const data = [
      {
        token: 'USDT',
        total: usdtAvailableBalance,
        totalUsd: usdtAvailableBalance * (usdtPrice || 1),
        tooltip: 'Amount of USDT available for withdrawal',
      },
      {
        token: 'OPV',
        total: opvAvailableBalance,
        totalUsd: opvAvailableBalance * mbcPrice * (usdtPrice || 1),
        tooltip: 'Amount of MBC daily bonus',
      },
    ]

    return {
      usdtBalance: usdtAvailableBalance || 0,
      opvBalance: opvAvailableBalance || 0,
      balanceData: data,
      percentUsdt: assetAllocationUsdt,
      percentOpv: assetAllocationOpv,
    }
  }, [balances, usdtPrice, mbcPrice])

  return (
    <Wrapper className="box-primary">
      <div className="flex md:flex-row flex-col md:items-center md:justify-between md:gap-0 gap-4 xl:px-6 py-6 px-4 border-b-2 border-solid stroke-brand-alpha-1">
        <div className="space-y-[10px]">
          <p className="text-[18px] leading-6 font-bold text-primary">{t('Funding wallet')}</p>

          <div className="flex md:flex-row flex-col md:items-center md:gap-4 gap-1">
            <div className="flex items-center gap-1 md:pr-4 md:border-r-2 md:border-solid md:stroke-sm">
              <p className="flex items-center gap-1 text-[14px] text-primary">
                <span className="text-secondary text-[14px]">{t('Wallet')}: </span>
                {formatCode(myWallet?.address, 5, 5)}
              </p>

              <Copy2Icon textCopy={myWallet?.address} className="cursor-pointer" />
            </div>

            <div className="flex items-center gap-1">
              <p className="flex items-center gap-1 text-[14px] text-primary">
                <span className="text-secondary text-[14px]">UID: </span>
                {formatCode(userData?.id, 5, 5)}
              </p>
              <Copy2Icon textCopy={userData?.id} className="cursor-pointer" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* <Link href="/wallet" passHref>
            <Button type="primary" className="active w-full md:w-[115px]">
              {t('Deposit')}
            </Button>
          </Link> */}
          {/* <Link href="/withdraw?wallet=opv_balance" passHref> */}
          <Link href="/withdraw?wallet=OPV" passHref>
            <Button ghost type="primary" className="w-full md:w-[115px]">
              {t('Withdraw')}
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid md:grid-cols-[2fr_1fr]">
        <div className="flex lg:flex-row flex-col lg:items-center md:gap-6 gap-3 xl:px-6 py-6 px-4 md:border-r-2 md:border-b-0 border-b-2 border-solid stroke-brand-alpha-1">
          <div className="flex-shrink-0 grid gap-1">
            <p className="text-[18px] text-secondary leading-6">{t('Estimated balance')}</p>
            <p className="text-[27px] leading-[1.5] font-bold text-text-brand">
              ~ ${addCommasToNumber(usdtBalance + opvBalance * mbcPrice)}
            </p>

            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <ArrowBackIcon
                  className={`${perCentBalance >= 0 ? 'rotate-[90deg]' : 'rotate-[-90deg]'} w-4`}
                  fill={perCentBalance >= 0 ? '#3AB500' : '#FF0F00'}
                />
                <p
                  className={`${perCentBalance >= 0 ? 'text-text-success' : 'text-text-danger'} text-[14px] leading-6`}
                >
                  {perCentBalance}%
                </p>
              </div>

              <Select
                className="w-[120px]"
                options={[
                  {
                    label: t('in 7 days'),
                    value: 7,
                  },
                  {
                    label: t('in 30 days'),
                    value: 30,
                  },
                  {
                    label: t('in 90 days'),
                    value: 90,
                  },
                ]}
                defaultValue={7}
                onChange={(val) => setParamBalance((prev) => ({ ...prev, range: val }))}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 flex-1 token">
            {balanceData?.map((item) => (
              <div
                className="px-4 py-3 bg-surface-background-1 rounded-lg backdrop-blur-[30px] flex items-center justify-between"
                key={item?.token}
              >
                <div className="flex gap-1 items-center">
                  <img className="w-7 bg-teal rounded-full" src={TOKEN_ICON_MAPPING?.[item?.token]} alt="" />
                  <p className="text-primary">{TOKEN_WITHDRAW?.[item?.token]}</p>
                  <Tooltip title={t(item?.tooltip)}>
                    <ExclamationCircleOutlined className="text-secondary w-[16px] h-[16px]" rev={undefined} />
                  </Tooltip>
                </div>

                <div className="flex md:flex-row flex-col gap-1 items-center">
                  <p className="value text-primary">{addCommasToNumber(item?.total)}</p>
                  <p className="rate text-secondary">≈${addCommasToNumber(item?.totalUsd)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="xl:px-6 py-6 px-4 flex lg:flex-row md:flex-col flex-row items-center gap-4">
          <div className="flex-shrink-0">
            <Progress
              percent={100}
              type="circle"
              success={{ percent: percentUsdt, strokeColor: '#00349D' }}
              showInfo={false}
              strokeColor="#25B8E9"
            />
          </div>

          <div className="lg:flex-1 md:flex-0 flex-1 grid gap-2">
            <p className="md:text-[18px] text-secondary leading-6">{t('Asset allocation')}</p>
            <div className="flex items-center space-x-[6px]">
              <div className="w-[12px] h-[12px] bg-brand-500 dark:bg-brand-400" />
              <p className="md:text-[18px] text-primary">USDT: {percentUsdt?.toFixed(2)}%</p>
            </div>
            <div className="flex items-center space-x-[6px]">
              <div className="w-[12px] h-[12px] bg-[#25B8E9]" />
              <p className="md:text-[18px] text-primary">MBC: {percentOpv?.toFixed(2)}%</p>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  )
}

export default FundingView
